import { Save, X } from "lucide-react";
import { useState } from "react";
import { OUTREACH_STATUS_OPTIONS } from "./OutreachTimeline";

interface OutreachFormValues {
  channel: string;
  direction: string;
  status: string;
  messageContent: string;
}

interface OutreachFormProps {
  onSave: (data: OutreachFormValues) => void;
  onCancel: () => void;
  saving: boolean;
  /** Prefill, e.g. "Mark as sent" from a draft (#3169). */
  initial?: Partial<OutreachFormValues>;
}

// Mirrors OutreachAttempt::CHANNELS / STATUSES. There is no "pending": this
// form logs contact that already happened outside Rarion (#3169).
const CHANNELS = ["email", "phone", "linkedin", "other"];
const DIRECTIONS = ["outbound", "inbound"];
const STATUSES = OUTREACH_STATUS_OPTIONS;

export function OutreachForm({ onSave, onCancel, saving, initial }: OutreachFormProps) {
  const [channel, setChannel] = useState(initial?.channel ?? "email");
  const [direction, setDirection] = useState(initial?.direction ?? "outbound");
  const [status, setStatus] = useState(initial?.status ?? "sent");
  const [messageContent, setMessageContent] = useState(initial?.messageContent ?? "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ channel, direction, status, messageContent });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded border border-border bg-card-alt p-3"
    >
      <p className="text-xs text-text-muted">
        Log a message you have already sent, or a reply you received, outside Rarion. Rarion does
        not send messages.
      </p>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary">Channel</label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Direction</label>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {DIRECTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary">Message</label>
        <textarea
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          placeholder="What was sent or received..."
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
        >
          <X size={12} /> Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          <Save size={12} /> {saving ? "Saving..." : "Log contact"}
        </button>
      </div>
    </form>
  );
}
